import {
	type ContentAssignmentDTO,
	ContentLocalizationStatus,
	type ContentLocalizationSummaryDTO,
	ContentScopeType,
	type ContentVersionDTO,
} from '@today/types'

/**
 * Derived data of the texts list (TO-420 content-admin-ui REQ-002). Every rule
 * takes `now` so tests pin time without faking the clock.
 */

/** A chip label: a localization status, or `missing` / `scheduled`. */
export type ChipLabel = ContentLocalizationStatus | 'missing' | 'scheduled'

export interface LocaleChip {
	locale: string
	label: ChipLabel
}

/** The version in force (and until when) plus the earliest scheduled publication. */
export interface InForceSummary {
	version: number | null
	until: string | null
	scheduled: { version: number; from: string } | null
}

/** Where a text is used: tenant scope, and the distinct products. */
export interface Usage {
	tenant: boolean
	productIds: string[]
}

export interface UsageLabel {
	tenant: boolean
	products: number
	unused: boolean
}

/** The list filters, mirrored one-to-one in the URL query. */
export interface TextListFilters {
	type: string
	status: string
	locale: string
	q: string
	pending: boolean
	expiring: boolean
	unused: boolean
	page: number
}

export const LIST_PAGE_SIZE = 20
/** "Expiring in 30 days" horizon (the TO-419 fixed expiry warning horizon). */
export const EXPIRY_DAYS = 30
const DAY_MS = 86_400_000

type Window = Pick<ContentLocalizationSummaryDTO, 'status' | 'validFrom' | 'validTo'>

const time = (iso: string) => new Date(iso).getTime()

const isScheduled = (loc: Pick<Window, 'status' | 'validFrom'>, now: Date) =>
	loc.status === ContentLocalizationStatus.PUBLISHED && loc.validFrom !== null && time(loc.validFrom) > now.getTime()

/** A published localization whose start is still ahead reads `scheduled`; otherwise its status. */
export const statusLabel = (loc: Pick<Window, 'status' | 'validFrom'>, now: Date): ChipLabel =>
	isScheduled(loc, now) ? 'scheduled' : loc.status

/** One chip per required locale (stored order), then every other locale of the latest version (ascending). */
export const localeChips = (required: readonly string[], latest: ContentVersionDTO, now: Date): LocaleChip[] => {
	const byLocale = new Map(latest.localizations.map((l) => [l.locale, l]))
	const extra = [...byLocale.keys()].filter((l) => !required.includes(l)).sort()
	return [...required, ...extra].map((locale) => {
		const loc = byLocale.get(locale)
		return { locale, label: loc ? statusLabel(loc, now) : 'missing' }
	})
}

/** Published, started (`validFrom ≤ now`) and not yet ended (`validTo` null or after now). */
export const isInForce = (loc: Window, now: Date): boolean =>
	loc.status === ContentLocalizationStatus.PUBLISHED &&
	loc.validFrom !== null &&
	time(loc.validFrom) <= now.getTime() &&
	(loc.validTo === null || time(loc.validTo) > now.getTime())

const earliest = (dates: string[]): string | null =>
	dates.reduce<string | null>((min, d) => (min === null || time(d) < time(min) ? d : min), null)

/** The highest version with an in-force localization, its earliest end, and the earliest scheduled one. */
export const inForceSummary = (versions: readonly ContentVersionDTO[], now: Date): InForceSummary => {
	const inForce = [...versions]
		.sort((a, b) => b.versionNumber - a.versionNumber)
		.find((v) => v.localizations.some((l) => isInForce(l, now)))
	const until = inForce
		? earliest(
				inForce.localizations.filter((l) => isInForce(l, now) && l.validTo !== null).map((l) => l.validTo as string),
			)
		: null
	let scheduled: InForceSummary['scheduled'] = null
	for (const v of versions) {
		for (const l of v.localizations) {
			if (isScheduled(l, now) && (scheduled === null || time(l.validFrom as string) < time(scheduled.from))) {
				scheduled = { version: v.versionNumber, from: l.validFrom as string }
			}
		}
	}
	return { version: inForce?.versionNumber ?? null, until, scheduled }
}

/** Live assignments aggregated per text id. */
export const usageByText = (assignments: readonly ContentAssignmentDTO[]): Map<string, Usage> => {
	const usage = new Map<string, Usage>()
	for (const a of assignments) {
		const entry = usage.get(a.textId) ?? { tenant: false, productIds: [] }
		if (a.scopeType === ContentScopeType.TENANT) entry.tenant = true
		else if (a.scopeId && !entry.productIds.includes(a.scopeId)) entry.productIds.push(a.scopeId)
		usage.set(a.textId, entry)
	}
	return usage
}

/** "Tenant · N products" parts, or unused. */
export const usageLabel = (usage: Usage | undefined): UsageLabel => {
	const tenant = usage?.tenant ?? false
	const products = usage?.productIds.length ?? 0
	return { tenant, products, unused: !tenant && products === 0 }
}

const flag = (search: URLSearchParams, key: string) => search.get(key) === '1'

/** Filters from the URL query; unknown or missing values fall back to the defaults. */
export const filtersFromSearch = (search: URLSearchParams): TextListFilters => {
	const page = Number(search.get('page'))
	return {
		type: search.get('type') ?? '',
		status: search.get('status') ?? '',
		locale: search.get('locale') ?? '',
		q: search.get('q') ?? '',
		pending: flag(search, 'pending'),
		expiring: flag(search, 'expiring'),
		unused: flag(search, 'unused'),
		page: Number.isInteger(page) && page > 0 ? page : 1,
	}
}

/** The URL query of the filters, omitting defaults. */
export const searchFromFilters = (filters: TextListFilters): URLSearchParams => {
	const search = new URLSearchParams()
	for (const key of ['type', 'status', 'locale', 'q'] as const) {
		if (filters[key]) search.set(key, filters[key])
	}
	for (const key of ['pending', 'expiring', 'unused'] as const) {
		if (filters[key]) search.set(key, '1')
	}
	if (filters.page > 1) search.set('page', String(filters.page))
	return search
}

/** The API filters of the list (no page/limit). */
export const apiFilters = (filters: TextListFilters, now: Date): Record<string, string> => {
	const params: Record<string, string> = {}
	if (filters.type) params['filters[typeCode]'] = filters.type
	if (filters.status) params['filters[status]'] = filters.status
	if (filters.locale) params['filters[locale]'] = filters.locale
	if (filters.q) params['filters[q]'] = filters.q
	if (filters.pending) params['filters[pendingApproval]'] = 'true'
	if (filters.expiring) params['filters[expiringBefore]'] = new Date(now.getTime() + EXPIRY_DAYS * DAY_MS).toISOString()
	return params
}

/** `GET /texts` query for one server page. */
export const listParams = (filters: TextListFilters, now: Date): Record<string, string> => ({
	...apiFilters(filters, now),
	page: String(filters.page),
	limit: String(LIST_PAGE_SIZE),
})

/** One local page of 20 (the "not used anywhere" filter pages client-side). */
export const localPage = <T>(rows: readonly T[], page: number): { data: T[]; totalPages: number } => ({
	data: rows.slice((page - 1) * LIST_PAGE_SIZE, page * LIST_PAGE_SIZE),
	totalPages: Math.ceil(rows.length / LIST_PAGE_SIZE),
})

/**
 * Per-language status of the version in force (product "Legal texts" list):
 * `published` when a localization is in force, else `scheduled`, else `missing`.
 * Locales: the required ones first, then every other published locale ascending.
 */
export const inForceChips = (
	required: readonly string[],
	versions: readonly ContentVersionDTO[],
	now: Date,
): LocaleChip[] => {
	const published = versions.flatMap((v) =>
		v.localizations.filter((l) => l.status === ContentLocalizationStatus.PUBLISHED),
	)
	const extra = [...new Set(published.map((l) => l.locale))].filter((l) => !required.includes(l)).sort()
	return [...required, ...extra].map((locale) => {
		const mine = published.filter((l) => l.locale === locale)
		if (mine.some((l) => isInForce(l, now))) return { locale, label: ContentLocalizationStatus.PUBLISHED }
		return { locale, label: mine.some((l) => isScheduled(l, now)) ? 'scheduled' : 'missing' }
	})
}
